import { createSlice } from "@reduxjs/toolkit";
import {
  BrandDetailResponseType,
  DefaultAuditRequestBodyType,
  DefaultDuplicateBrandRequestBodyType,
  DefaultManualCheckCompleteRequestBodyType,
  DefaultMergedBrandProcessLogRequestBodyType,
  DefaultMergedBrandRequestBodyType,
  DefaultRequestBodyType,
  defaultAuditRequestBody,
  defaultDuplicateBrandRequestBody,
  defaultManualCheckCompleteRequestBodyType,
  defaultMergedBrandProcessLogRequestBodyType,
  defaultMergedBrandRequestBody,
  defaultRequestBody,
} from "../../constant/logoProductionType";
import {
  fetchLogos,
  fetchPaginateLogos,
  updateRequestBody,
  resetTable,
  fetchRefreshDocuments,
  fetchBrandDetail,
  saveBrandDetail,
  saveImageDetail,
  deleteImage,
  acceptImage,
  setCurrentImageIndex,
  setAutoRefresh,
  switchAutoFresh,
  stopAutoRefresh,
  addSymbol,
  updateStockSymbol,
  editSymbol,
  deleteSymbol,
  setStockSymbols,
  cleanBrandDetail,
  setIsEdited,
  openDeleteStockSymbolModal,
  closeDeleteStockSymbolModal,
  openEditStockSymbolModal,
  closeEditStockSymbolModal,
  openAddStockSymbolModal,
  closeAddStockSymbolModal,
  openConfirmDeleteModal,
  closeConfirmDeleteModal,
  openUnsavedChangesModal,
  closeUnsavedChangesModal,
  resetAuditEntryTable,
  updateAuditRequestBody,
  fetchAuditEntriesDetails,
  fetchAuditPaginateEntries,
  saveIndustryDetail,
  openDeleteBrandModal,
  closeDeleteBrandModal,
  openArchiveBrandModal,
  closeArchiveBrandModal,
  openUnArchiveBrandModal,
  closeUnArchiveBrandModal,
  createBrandOpenModal,
  createBrandCloseModal,
  fetchDuplicateBrand,
  fetchPaginateDuplicateBrand,
  openCreateMergeRequestModal,
  closeCreateMergeRequestModal,
  fetchCreateMergeBrandDetails,
  fetchMergeRequestBrandList,
  fetchPaginateMergeBrandList,
  resetDuplicateBrandTable,
  resetMergeRequestBrandTable,
  updateDuplicateBrandRequestBody,
  updateMergeRequestBrandListRequestBody,
  fetchMergedBrandProcessDetails,
  updateRequestBodyForDuplicateTable,
  updateRequestBodyForMergeRequestTable,
  resetMergeRequestProcessLogTable,
  getSelectedMergedBrandProcessLogDetails,
  openMergeRequestBrandProcessLog,
  closeMergeRequestBrandProcessLog,
  completeManualCheck,
  createMergeRequest,
  resetCreateMergeRequestStatus,
  fetchPaginateMergedBrandProcessDetails,
} from "./LivePlatformAction";

export interface PaginationType {
  page: number;
  pageSize: number;
  hasMore: boolean;
  loading: boolean;
}
interface AutoRefreshStateType {
  autoRefresh: boolean;
  currentIntervalId: NodeJS.Timer | undefined;
  error: string | undefined;
  loading: boolean;
}
interface BrandDetailStateType {
  loading: boolean;
  saveLoading: boolean;
  error: string | undefined;
  currentStockSymbol: {
    symbol: string;
    exchangeName: string;
    id: number;
    index: number;
    isin: string;
  };
  stockSymbolSelectedToDelete: {
    symbol: string;
    exchangeName: string;
    id: number;
    isin: string;
  };
  data: BrandDetailResponseType;
  dropDownData: {
    country: { value: string; label: string }[];
    stockExchange: { value: string; label: string }[];
    imageMetaData: any;
    sectorList: any;
  };
  isEdited: boolean;
  openStockSymbolModalState: boolean;
  openCreateBrandModalState: boolean;
  openEditStockSymbolModalState: boolean;
  openDeleteStockSymbolModalState: boolean;
  openConfirmDeleteModalState: boolean;
  openUnsavedChangesModalState: boolean;
  openGoLiveConfirmModalState: boolean;
  currentImageIndex: number;
  imageViewerLoading: boolean;
  openConfirmBrandDeleteModalState: boolean;
  openConfirmArchiveBrandModalState: boolean;
  openConfirmUnArchiveBrandModalState: boolean;
}
interface AuditEntriesStateType {
  data: any;
  loading: boolean;
  error: string | undefined;
  currentRequestBody: DefaultAuditRequestBodyType;
  pagination: PaginationType;
  id: number;
  createdAt: string;
  createdBy: string;
  shortMessage: string;
  changes: string;
}
interface createMergeRequestStatusType {
  data: any;
  loading: boolean;
  error: string | undefined;
}
interface CreateMergeDuplicateBrandStateType {
  data: any;
  loading: boolean;
  error: string | undefined;
  createMergeRequestModalState: boolean;
  duplicateBrandDetails: DuplicateBrandDetailsType;
  createMergeRequestStatus: createMergeRequestStatusType;
}
interface DuplicateBrandDetailsType {
  website: string;
  duplicateBrands: { key: number; value: string }[]; // Array of objects with key and value
  brandsCount: number;
  imagesCount: number;
}
interface MergedDuplicateBrandDetailsType {
  data: any;
  loading: boolean;
  error: string | undefined;
  pagination: PaginationType;
  currentRequestBody: DefaultMergedBrandRequestBodyType;
  mergeBrandDetails: any;
}
interface mergedDuplicateBrandProcessLogDetailsType {
  data: any;
  loading: boolean;
  error: string | undefined;
  pagination: PaginationType;
  currentRequestBody: DefaultMergedBrandProcessLogRequestBodyType;
  mergedBrandProcessDetails: any;
  openMergedDuplicateBrandProcessLogModalState: boolean;
}
interface manualCheckCompleteDetailsType {
  data: any;
  loading: boolean;
  error: string | undefined;
  currentRequestBody: DefaultManualCheckCompleteRequestBodyType;
}

interface DuplicateBrandStateType {
  data: any;
  loading: boolean;
  error: string | undefined;
  currentRequestBody: DefaultDuplicateBrandRequestBodyType;

  pagination: PaginationType;
  duplicateBrandDetails: DuplicateBrandDetailsType[];
  CreateMergeDuplicateBrandState: CreateMergeDuplicateBrandStateType;
  mergedDuplicateBrandDetails: MergedDuplicateBrandDetailsType;
  mergedDuplicateBrandProcessLogDetails: mergedDuplicateBrandProcessLogDetailsType;
  manualCheckCompleteDetails: manualCheckCompleteDetailsType;
}
interface LivePlatformStateType {
  data: any;
  loading: boolean;
  error: string | undefined;
  currentRequestBody: DefaultRequestBodyType;
  pagination: PaginationType;
  autoRefreshState: AutoRefreshStateType;
  auditEntriesState: AuditEntriesStateType;
  brandDetailState: BrandDetailStateType;
  duplicateBrandState: DuplicateBrandStateType;
}

const livePlatFormInitialState: LivePlatformStateType = {
  data: [],
  loading: false,
  error: undefined,
  currentRequestBody: defaultRequestBody,
  pagination: {
    page: 2,
    pageSize: 30,
    hasMore: true,
    loading: false,
  },
  autoRefreshState: {
    autoRefresh: false,
    currentIntervalId: undefined,
    error: "",
    loading: false,
  },
  auditEntriesState: {
    data: [],
    loading: false,
    error: "",
    currentRequestBody: defaultAuditRequestBody,
    pagination: {
      page: 2,
      pageSize: 30,
      hasMore: true,
      loading: false,
    },
    id: 0,
    createdAt: "",
    createdBy: "",
    shortMessage: "",
    changes: "",
  },
  duplicateBrandState: {
    data: [],
    loading: false,
    error: "",
    currentRequestBody: defaultDuplicateBrandRequestBody,

    pagination: {
      page: 2,
      pageSize: 30,
      hasMore: true,
      loading: false,
    },
    duplicateBrandDetails: [],
    CreateMergeDuplicateBrandState: {
      data: [],
      loading: false,
      error: "",
      createMergeRequestModalState: false,
      duplicateBrandDetails: {
        website: "",
        duplicateBrands: [],
        brandsCount: 0,
        imagesCount: 0,
      },
      createMergeRequestStatus: {
        data: [],
        loading: false,
        error: "",
      },
    },
    mergedDuplicateBrandDetails: {
      data: [],
      loading: false,
      error: "",
      currentRequestBody: defaultMergedBrandRequestBody,
      pagination: {
        page: 2,
        pageSize: 30,
        hasMore: true,
        loading: false,
      },
      mergeBrandDetails: [],
    },
    mergedDuplicateBrandProcessLogDetails: {
      data: [],
      loading: false,
      error: "",
      currentRequestBody: defaultMergedBrandProcessLogRequestBodyType,
      pagination: {
        page: 2,
        pageSize: 30,
        hasMore: true,
        loading: false,
      },
      mergedBrandProcessDetails: [],
      openMergedDuplicateBrandProcessLogModalState: false,
    },
    manualCheckCompleteDetails: {
      data: [],
      loading: false,
      error: "",
      currentRequestBody: defaultManualCheckCompleteRequestBodyType,
    },
  },
  brandDetailState: {
    loading: false,
    error: "",
    currentStockSymbol: {
      symbol: "",
      exchangeName: "",
      id: 0,
      isin: "",
      index: 0,
    },
    stockSymbolSelectedToDelete: {
      symbol: "",
      exchangeName: "",
      isin: "",
      id: 0,
    },
    data: {
      id: 0,
      name: "",
      domain: "",
      stockSymbols: [],
      isin: "",
      isArchived: false,
      isDummyDomain: false,
      // country: "",
      status: "",
      batchId: 0,

      description: "",

      images: [
        {
          id: 0,
          type: "",
          src: "",
          opacity: "",
          format: "",
          size: 0,
          height: 0,
          width: 0,
          color: "",
          theme: "",
          status: "",
          comments: "",
          fileName: "",
          isArchived: false,
        },
      ],
      tags: [],
      sectors: [],
      industries: [],
      brandLocation: {
        id: 4,
        brandId: 314436,
        city: "",
        state: "",
        country: "",
        countryCode: "",
        subRegion: "",
        region: "",
      },
    },
    dropDownData: {
      country: [],
      stockExchange: [],
      imageMetaData: {},
      sectorList: [],
    },
    isEdited: false,
    saveLoading: false,
    openStockSymbolModalState: false,
    openConfirmDeleteModalState: false,
    openConfirmBrandDeleteModalState: false,
    openConfirmArchiveBrandModalState: false,
    openConfirmUnArchiveBrandModalState: false,
    openCreateBrandModalState: false,
    openEditStockSymbolModalState: false,
    openDeleteStockSymbolModalState: false,
    openUnsavedChangesModalState: false,
    openGoLiveConfirmModalState: false,
    currentImageIndex: 0,
    imageViewerLoading: false,
  },
};

const livePlatformSlice = createSlice({
  initialState: livePlatFormInitialState,
  name: "logoProduction",
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchLogos.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchLogos.fulfilled, (state, action) => {
        state.data = action.payload.data;
        state.loading = false;
      })
      .addCase(fetchLogos.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(fetchPaginateLogos.pending, (state, action) => {
        state.pagination.loading = true;
      })
      .addCase(fetchPaginateLogos.fulfilled, (state, action) => {
        state.pagination.page = state.pagination.page + 1;
        state.data = state.data.concat(action.payload.data);
        state.pagination.hasMore =
          action.payload.data.length === state.pagination.pageSize;
        state.pagination.loading = false;
      })
      .addCase(fetchPaginateLogos.rejected, (state, action) => {
        state.error = action.error.message;
        state.pagination.loading = false;
      })
      .addCase(updateRequestBody, (state, action) => {
        state.currentRequestBody = action.payload;
      })
      .addCase(resetTable, (state, action) => {
        return {
          ...livePlatFormInitialState,
          data: state.data,
          currentRequestBody: state.currentRequestBody,
        };
      })
      .addCase(fetchRefreshDocuments.pending, (state, action) => {
        state.autoRefreshState.loading = true;
      })
      .addCase(fetchRefreshDocuments.fulfilled, (state, action) => {
        state.data = action.payload.data;
        state.autoRefreshState.loading = false;
      })
      .addCase(fetchRefreshDocuments.rejected, (state, action) => {
        state.autoRefreshState.error = action.error.message;
        state.autoRefreshState.loading = false;
      })
      .addCase(fetchBrandDetail.pending, (state, action) => {
        state.brandDetailState.loading = true;
      })
      .addCase(fetchBrandDetail.fulfilled, (state, action) => {
        state.brandDetailState.data = action.payload.brandDetail.data;
        state.brandDetailState.dropDownData = action.payload.dropdownData;
        state.brandDetailState.loading = false;
      })

      .addCase(fetchBrandDetail.rejected, (state, action) => {
        state.brandDetailState.error = action.error.message;
        state.brandDetailState.loading = false;
      })
      .addCase(saveBrandDetail.pending, (state, action) => {
        state.brandDetailState.saveLoading = true;
      })
      .addCase(saveBrandDetail.fulfilled, (state, action) => {
        state.brandDetailState.data = {
          ...state.brandDetailState.data,
          ...action.payload,
          images: state.brandDetailState.data.images,
          sectors: state.brandDetailState.data.sectors,
          industries: state.brandDetailState.data.industries,
        };
        state.brandDetailState.saveLoading = false;
      })
      .addCase(saveBrandDetail.rejected, (state, action) => {
        state.brandDetailState.error = action.error.message;
        state.brandDetailState.saveLoading = false;
      })
      //industry details
      .addCase(saveIndustryDetail.pending, (state, action) => {
        state.brandDetailState.saveLoading = true;
      })
      // .addCase(saveIndustryDetail.fulfilled, (state, action) => {
      //   state.brandDetailState.data = {
      //     ...action.payload,
      //     images: state.brandDetailState.data.images,
      //   };
      //   state.brandDetailState.saveLoading = false;
      // })
      .addCase(saveIndustryDetail.fulfilled, (state, action) => {
        state.brandDetailState.data = {
          ...state.brandDetailState.data, // Retain all existing properties
          ...action.payload, // Overwrite with any new data from the action payload
          images: state.brandDetailState.data.images, // Ensure images are not overwritten
        };
        state.brandDetailState.saveLoading = false;
      })

      .addCase(saveIndustryDetail.rejected, (state, action) => {
        state.brandDetailState.error = action.error.message;
        state.brandDetailState.saveLoading = false;
      })
      .addCase(saveImageDetail.pending, (state, action) => {
        state.brandDetailState.saveLoading = true;
      })
      .addCase(saveImageDetail.fulfilled, (state, action) => {
        state.brandDetailState.data.images[
          state.brandDetailState.currentImageIndex
        ] = {
          ...action.payload,
          src: state.brandDetailState.data.images[
            state.brandDetailState.currentImageIndex
          ].src,
          status:
            state.brandDetailState.data.images[
              state.brandDetailState.currentImageIndex
            ].status,
        };
        state.brandDetailState.saveLoading = false;
      })
      .addCase(saveImageDetail.rejected, (state, action) => {
        state.brandDetailState.error = action.error.message;
        state.brandDetailState.saveLoading = false;
      })
      .addCase(deleteImage.pending, (state, action) => {
        state.brandDetailState.imageViewerLoading = true;
      })
      .addCase(deleteImage.fulfilled, (state, action) => {
        // console.log(state.brandDetailState.data.images.length, "length 1");
        // console.log(state.brandDetailState.currentImageIndex, "current index 1");
        // console.log(state.brandDetailState.data.images, "image 1");
        // console.log(state.brandDetailState.data.images.length, "length 2");
        // console.log(state.brandDetailState.currentImageIndex, "current index 2");
        state.brandDetailState.data.images.splice(
          state.brandDetailState.currentImageIndex,
          1
        );
        // state.brandDetailState.data.images = state.brandDetailState.data.images.splice(state.brandDetailState.data.images.length - 1, 1);

        // console.log(state.brandDetailState.data.images, "image 2");
        // console.log(state.brandDetailState.data.images.length === state.brandDetailState.currentImageIndex, "check this condtion");
        state.brandDetailState.currentImageIndex =
          state.brandDetailState.data.images.length ===
          state.brandDetailState.currentImageIndex
            ? 0
            : state.brandDetailState.currentImageIndex;
        state.brandDetailState.imageViewerLoading = false;
      })

      .addCase(deleteImage.rejected, (state, action) => {
        state.brandDetailState.error = action.error.message;
        state.brandDetailState.imageViewerLoading = false;
      })
      .addCase(acceptImage.pending, (state, action) => {
        state.brandDetailState.imageViewerLoading = true;
      })
      .addCase(acceptImage.fulfilled, (state, action) => {
        state.brandDetailState.data.images[
          state.brandDetailState.currentImageIndex
        ] = {
          ...state.brandDetailState.data.images[
            state.brandDetailState.currentImageIndex
          ],
          status: "Live",
        };
        state.brandDetailState.currentImageIndex =
          state.brandDetailState.data.images.length ===
          state.brandDetailState.currentImageIndex + 1
            ? 0
            : state.brandDetailState.currentImageIndex + 1;
        state.brandDetailState.imageViewerLoading = false;
      })
      .addCase(acceptImage.rejected, (state, action) => {
        state.brandDetailState.error = action.error.message;
        state.brandDetailState.imageViewerLoading = false;
      })

      .addCase(setCurrentImageIndex, (state, action) => {
        state.brandDetailState.currentImageIndex = action.payload;
      })

      .addCase(setAutoRefresh, (state, action) => {
        state.autoRefreshState.currentIntervalId = action.payload.intervalId;
      })
      .addCase(switchAutoFresh, (state, action) => {
        state.autoRefreshState.autoRefresh = action.payload.autoRefresh;
      })
      .addCase(stopAutoRefresh, (state, action) => {
        clearInterval(state.autoRefreshState.currentIntervalId);
      })
      .addCase(addSymbol, (state, action) => {
        state.brandDetailState.data.stockSymbols.push(action.payload);
      })
      .addCase(updateStockSymbol, (state, action) => {
        state.brandDetailState.currentStockSymbol = action.payload;
      })
      .addCase(editSymbol, (state, action) => {
        state.brandDetailState.data.stockSymbols.forEach((element, index) => {
          if (index === state.brandDetailState.currentStockSymbol.index) {
            state.brandDetailState.data.stockSymbols[index] = {
              ...state.brandDetailState.data.stockSymbols[index],
              symbol: action.payload.symbol,
              exchangeName: action.payload.exchangeName,
              isin: action.payload.isin,
            };
          }
        });
      })
      .addCase(deleteSymbol, (state, action) => {
        const updatedStockSymbols =
          state.brandDetailState.data.stockSymbols.filter(
            (element, idx) =>
              idx !== state.brandDetailState.currentStockSymbol.index
          );
        state.brandDetailState.data.stockSymbols = updatedStockSymbols;
      })
      .addCase(setStockSymbols, (state, action) => {
        state.brandDetailState.data.stockSymbols = action.payload;
      })

      .addCase(cleanBrandDetail, (state, action) => {
        state.brandDetailState = livePlatFormInitialState.brandDetailState;
      })
      .addCase(setIsEdited, (state, action) => {
        state.brandDetailState.isEdited = action.payload;
      })

      .addCase(openDeleteStockSymbolModal, (state, action) => {
        state.brandDetailState.openDeleteStockSymbolModalState = true;
      })
      .addCase(closeDeleteStockSymbolModal, (state, action) => {
        state.brandDetailState.openDeleteStockSymbolModalState = false;
      })
      .addCase(openEditStockSymbolModal, (state, action) => {
        state.brandDetailState.openEditStockSymbolModalState = true;
      })
      .addCase(closeEditStockSymbolModal, (state, action) => {
        state.brandDetailState.openEditStockSymbolModalState = false;
      })
      .addCase(openAddStockSymbolModal, (state, action) => {
        state.brandDetailState.openStockSymbolModalState = true;
      })
      .addCase(closeAddStockSymbolModal, (state, action) => {
        state.brandDetailState.openStockSymbolModalState = false;
      })
      .addCase(createBrandOpenModal, (state, action) => {
        state.brandDetailState.openCreateBrandModalState = true;
      })
      .addCase(createBrandCloseModal, (state, action) => {
        state.brandDetailState.openCreateBrandModalState = false;
      })
      .addCase(openConfirmDeleteModal, (state, action) => {
        state.brandDetailState.openConfirmDeleteModalState = true;
      })
      .addCase(closeConfirmDeleteModal, (state, action) => {
        state.brandDetailState.openConfirmDeleteModalState = false;
      })
      .addCase(openUnsavedChangesModal, (state, action) => {
        state.brandDetailState.openUnsavedChangesModalState = true;
      })
      .addCase(closeUnsavedChangesModal, (state, action) => {
        state.brandDetailState.openUnsavedChangesModalState = false;
      })

      .addCase(resetAuditEntryTable, (state, action) => {
        state.auditEntriesState = {
          ...livePlatFormInitialState.auditEntriesState,
          data: state.auditEntriesState.data,
        };
      })
      .addCase(updateAuditRequestBody, (state, action) => {
        state.auditEntriesState.currentRequestBody = action.payload;
      })
      .addCase(fetchAuditEntriesDetails.pending, (state, action) => {
        state.auditEntriesState.loading = true;
      })
      .addCase(fetchAuditEntriesDetails.fulfilled, (state, action) => {
        state.auditEntriesState.data = action.payload.results;
        // state.auditEntriesState.dropDownData = action.payload.dropdownData;
        state.auditEntriesState.loading = false;
      })

      .addCase(fetchAuditEntriesDetails.rejected, (state, action) => {
        state.auditEntriesState.error = action.error.message;
        state.auditEntriesState.loading = false;
      })
      .addCase(fetchAuditPaginateEntries.pending, (state, action) => {
        state.auditEntriesState.pagination.loading = true;
      })
      .addCase(fetchAuditPaginateEntries.fulfilled, (state, action) => {
        state.auditEntriesState.pagination.page =
          state.auditEntriesState.pagination.page + 1;
        // state.auditEntriesState.data = [...state.auditEntriesState.data, ...action.payload.results];
        state.auditEntriesState.data = state.auditEntriesState.data.concat(
          action.payload.data.results
        );
        state.auditEntriesState.pagination.hasMore =
          action.payload.length === state.auditEntriesState.pagination.pageSize;
        state.auditEntriesState.pagination.loading = false;
      })
      .addCase(fetchAuditPaginateEntries.rejected, (state, action) => {
        // state.auditEntriesState.error = action.error.message;
        state.auditEntriesState.pagination.loading = false;
      })
      .addCase(openDeleteBrandModal, (state, action) => {
        state.brandDetailState.openConfirmBrandDeleteModalState = true;
      })
      .addCase(closeDeleteBrandModal, (state, action) => {
        state.brandDetailState.openConfirmBrandDeleteModalState = false;
      })
      .addCase(openArchiveBrandModal, (state, action) => {
        state.brandDetailState.openConfirmArchiveBrandModalState = true;
      })
      .addCase(closeArchiveBrandModal, (state, action) => {
        state.brandDetailState.openConfirmArchiveBrandModalState = false;
      })
      .addCase(openUnArchiveBrandModal, (state, action) => {
        state.brandDetailState.openConfirmUnArchiveBrandModalState = true;
      })
      .addCase(closeUnArchiveBrandModal, (state, action) => {
        state.brandDetailState.openConfirmUnArchiveBrandModalState = false;
      })
      .addCase(fetchDuplicateBrand.pending, (state, action) => {
        state.duplicateBrandState.loading = true;
      })
      .addCase(updateDuplicateBrandRequestBody, (state, action) => {
        state.duplicateBrandState.currentRequestBody = {
          ...state.duplicateBrandState.currentRequestBody,
          ...action.payload,
        };
      })
      .addCase(fetchDuplicateBrand.fulfilled, (state, action) => {
        state.duplicateBrandState.duplicateBrandDetails = action.payload.data;
        state.duplicateBrandState.loading = false;
      })
      .addCase(fetchDuplicateBrand.rejected, (state, action) => {
        state.duplicateBrandState.error = action.error.message;
        state.duplicateBrandState.loading = false;
      })

      .addCase(fetchPaginateDuplicateBrand.pending, (state, action) => {
        state.duplicateBrandState.pagination.loading = true;
      })
      .addCase(fetchPaginateDuplicateBrand.fulfilled, (state, action) => {
        state.duplicateBrandState.pagination.page =
          state.duplicateBrandState.pagination.page + 1;
        state.duplicateBrandState.duplicateBrandDetails =
          state.duplicateBrandState.duplicateBrandDetails.concat(
            action.payload.data
          );
        state.duplicateBrandState.pagination.hasMore =
          action.payload.data.length ===
          state.duplicateBrandState.pagination.pageSize;
        state.duplicateBrandState.pagination.loading = false;
      })
      .addCase(fetchPaginateDuplicateBrand.rejected, (state, action) => {
        state.duplicateBrandState.error = action.error.message;
        state.duplicateBrandState.pagination.loading = false;
      })

      .addCase(openCreateMergeRequestModal, (state, action) => {
        state.duplicateBrandState.CreateMergeDuplicateBrandState.createMergeRequestModalState =
          true;
      })
      .addCase(closeCreateMergeRequestModal, (state, action) => {
        state.duplicateBrandState.CreateMergeDuplicateBrandState.createMergeRequestModalState =
          false;
      })
      .addCase(fetchCreateMergeBrandDetails.pending, (state, action) => {
        state.duplicateBrandState.CreateMergeDuplicateBrandState.loading = true;
      })
      .addCase(fetchCreateMergeBrandDetails.fulfilled, (state, action) => {
        state.duplicateBrandState.CreateMergeDuplicateBrandState.duplicateBrandDetails =
          action.payload.data;
        state.duplicateBrandState.CreateMergeDuplicateBrandState.loading =
          false;
      })

      .addCase(fetchCreateMergeBrandDetails.rejected, (state, action) => {
        state.duplicateBrandState.CreateMergeDuplicateBrandState.error =
          action.error.message;
        state.duplicateBrandState.CreateMergeDuplicateBrandState.loading =
          false;
      })
      .addCase(fetchMergeRequestBrandList.pending, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandDetails.loading = true;
      })
      .addCase(fetchMergeRequestBrandList.fulfilled, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandDetails.mergeBrandDetails =
          action.payload.data;
        state.duplicateBrandState.mergedDuplicateBrandDetails.loading = false;
      })

      .addCase(fetchMergeRequestBrandList.rejected, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandDetails.error =
          action.error.message;
        state.duplicateBrandState.mergedDuplicateBrandDetails.loading = false;
      })
      .addCase(updateMergeRequestBrandListRequestBody, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandDetails.currentRequestBody =
          {
            ...state.duplicateBrandState.mergedDuplicateBrandDetails
              .currentRequestBody,
            ...action.payload,
          };
      })
      .addCase(fetchPaginateMergeBrandList.pending, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandDetails.pagination.loading =
          true;
      })
      .addCase(fetchPaginateMergeBrandList.fulfilled, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandDetails.pagination.page =
          state.duplicateBrandState.mergedDuplicateBrandDetails.pagination
            .page + 1;
        state.duplicateBrandState.mergedDuplicateBrandDetails.mergeBrandDetails =
          state.duplicateBrandState.mergedDuplicateBrandDetails.mergeBrandDetails.concat(
            action.payload.data
          );
        state.duplicateBrandState.mergedDuplicateBrandDetails.pagination.hasMore =
          action.payload.data.length ===
          state.duplicateBrandState.mergedDuplicateBrandDetails.pagination
            .pageSize;
        state.duplicateBrandState.mergedDuplicateBrandDetails.pagination.loading =
          false;
      })
      .addCase(fetchPaginateMergeBrandList.rejected, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandDetails.error =
          action.error.message;
        state.duplicateBrandState.mergedDuplicateBrandDetails.pagination.loading =
          false;
      })
      .addCase(resetDuplicateBrandTable, (state, action) => {
        return {
          ...state,
          duplicateBrandState: {
            ...state.duplicateBrandState,
            duplicateBrandDetails: [],
            pagination: {
              ...livePlatFormInitialState.duplicateBrandState.pagination,
            },
            loading: false,
            error: "",
            currentRequestBody: { ...defaultDuplicateBrandRequestBody },
          },
        };
      })
      .addCase(resetMergeRequestBrandTable, (state, action) => {
        return {
          ...state,
          duplicateBrandState: {
            ...state.duplicateBrandState,
            mergedDuplicateBrandDetails: {
              ...state.duplicateBrandState.mergedDuplicateBrandDetails,
              mergeBrandDetails: [],
              pagination: {
                ...livePlatFormInitialState.duplicateBrandState
                  .mergedDuplicateBrandDetails.pagination,
              },
              loading: false,
              error: "",
              currentRequestBody: { ...defaultMergedBrandRequestBody },
            },
          },
        };
      })
        .addCase(resetMergeRequestProcessLogTable, (state, action) => {
               state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails = {
                ...state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails,
                data: [],
                pagination: {
                  page: 2,
                  pageSize: 30,
                  hasMore: true,
                  loading: false,
                },
              };
            })
      .addCase(updateRequestBodyForDuplicateTable, (state, action) => {
        state.duplicateBrandState.currentRequestBody = {
          ...state.duplicateBrandState.currentRequestBody,
          ...action.payload,
        };
      })
      .addCase(updateRequestBodyForMergeRequestTable, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandDetails.currentRequestBody =
          {
            ...state.duplicateBrandState.mergedDuplicateBrandDetails
              .currentRequestBody,
            ...action.payload,
          };
      })
      .addCase(fetchMergedBrandProcessDetails.pending, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.loading =
          true;
      })
      .addCase(fetchMergedBrandProcessDetails.fulfilled, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.mergedBrandProcessDetails =
          action.payload.results;
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.loading =
          false;
      })

      .addCase(fetchMergedBrandProcessDetails.rejected, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.error =
          action.error.message;
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.loading =
          false;
      })
        .addCase(fetchPaginateMergedBrandProcessDetails.pending, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.pagination.loading =
          true;
      })
      .addCase(fetchPaginateMergedBrandProcessDetails.fulfilled, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.pagination.page =
          state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.pagination
            .page + 1;
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.mergedBrandProcessDetails =
          state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.mergedBrandProcessDetails.concat(
            action.payload.data
          );
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.pagination.hasMore =
          action.payload.data.length ===
          state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.pagination
            .pageSize;
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.pagination.loading =
          false;
      })
      .addCase(fetchPaginateMergedBrandProcessDetails.rejected, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.error =
          action.error.message;
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.pagination.loading =
          false;
      })
      .addCase(openMergeRequestBrandProcessLog, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.openMergedDuplicateBrandProcessLogModalState =
          true;
      })
      .addCase(closeMergeRequestBrandProcessLog, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.openMergedDuplicateBrandProcessLogModalState =
          false;
      })
      .addCase(getSelectedMergedBrandProcessLogDetails, (state, action) => {
        const index = action.payload;
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.data =
          state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.mergedBrandProcessDetails[
            index
          ];
      })
      .addCase(completeManualCheck.pending, (state, action) => {
        state.duplicateBrandState.manualCheckCompleteDetails.loading = true;
      })
      .addCase(completeManualCheck.fulfilled, (state, action) => {
        state.duplicateBrandState.manualCheckCompleteDetails.data =
          action.payload.data;
        state.duplicateBrandState.manualCheckCompleteDetails.loading = false;
      })

      .addCase(completeManualCheck.rejected, (state, action) => {
        state.duplicateBrandState.mergedDuplicateBrandProcessLogDetails.error =
          action.error.message;
        state.duplicateBrandState.manualCheckCompleteDetails.loading = false;
      })

      .addCase(createMergeRequest.pending, (state, action) => {
        state.duplicateBrandState.CreateMergeDuplicateBrandState.createMergeRequestStatus.loading =
          true;
      })
      .addCase(createMergeRequest.fulfilled, (state, action) => {
        state.duplicateBrandState.CreateMergeDuplicateBrandState.createMergeRequestStatus.data =
          action.payload.data;
        state.duplicateBrandState.CreateMergeDuplicateBrandState.createMergeRequestStatus.loading =
          false;
      })

      .addCase(createMergeRequest.rejected, (state, action: any) => {
        state.duplicateBrandState.CreateMergeDuplicateBrandState.createMergeRequestStatus.error =
          action.payload.detailedMessages[0];
        state.duplicateBrandState.CreateMergeDuplicateBrandState.createMergeRequestStatus.loading =
          false;
      })
      .addCase(resetCreateMergeRequestStatus, (state, action) => {
         state.duplicateBrandState.CreateMergeDuplicateBrandState.createMergeRequestStatus.error=
          "";
        state.duplicateBrandState.CreateMergeDuplicateBrandState.createMergeRequestStatus.data =
          null;
        state.duplicateBrandState.CreateMergeDuplicateBrandState.createMergeRequestStatus.loading =
          false;
      });
  },
});
export default livePlatformSlice.reducer;
